<template>
  <page-wrapper title="รายงาน Earn Transfer Home" ref="pageEl">
    <div class="page-container row" ref="page-container">

      <!-- Loading Section -->
      <LoadingOverlay :active="isLoading" />

      <!-- Report Section -->
      <div class="report-filter col-12">
          <div class="form">
            <div class="section">
              <div>
                <div class="label">วันที่โอนกรรมสิทธิ์</div>
                <date-picker
                  v-model="transferDateRange"
                  :disabled="!isDropdownDisabled"
                  type="daterange"
                ></date-picker>
              </div>
              <div>
                <div class="label">วันที่ได้รับ MNC</div>
                <date-picker
                  v-model="receiveMNCDateRange"
                  :disabled="!isDropdownDisabled"
                  type="daterange"
                ></date-picker>
              </div>
              <div>
                <div class="label">วันที่ Expired</div>
                <date-picker
                  v-model="expiredDateRange"
                  :disabled="!isDropdownDisabled"
                  type="daterange"
                ></date-picker>
              </div>
              <div>
                <div class="label">วันที่ Refund</div>
                <date-picker
                  v-model="refundDateRange"
                  :disabled="!isDropdownDisabled"
                  type="daterange"
                ></date-picker>
              </div>
            </div>

            <div class="section">
              <div>
                <div class="label">โครงการ</div>
                <project-select
                  v-model="selectedProject"
                  :disabled="!isDropdownDisabled"
                  v-bind:selectedProjectCode="selectedProject"
                >
                </project-select>
              </div>
              <div>
                <div class="label">บริษัท</div>
                <el-select
                  v-model="selectedCompany"
                  :disabled="!isDropdownDisabled"
                  style="width: 100%"
                  clearable
                  filterable
                >
                  <el-option
                    v-for="(t, idx) in filterCompany"
                    :key="idx"
                    :label="t.label"
                    :value="t.value"
                  ></el-option>
                </el-select>
              </div>
              <div>
                <div class="label">Transaction Status</div>
                <el-select
                  v-model="selectedTransactionStatus"
                  :disabled="!isDropdownDisabled"
                  style="width: 100%"
                  clearable
                  filterable
                >
                  <el-option
                    v-for="(t, idx) in filterTransactionStatus"
                    :key="idx"
                    :label="t.label"
                    :value="t.value"
                  ></el-option>
                </el-select>
              </div>
            </div>

            <div class="section">
              <div>
                <div class="label">
                  Transaction Type
                </div>
                <el-checkbox-group
                  v-model="selectedTransactionType"
                  style="
                    display: flex;
                    flex-direction: column;
                  ">
                    <el-checkbox
                      v-for="(time, idx) in filterTransactionType"
                      :label="time.value"
                      :key="idx"
                    >{{ time.label }}</el-checkbox>
                </el-checkbox-group>
              </div>
            </div>

            <div style="
              display: flex;
              justify-content: center;
            ">

              <el-button
                type="primary"
                :disabled="
                  !isSearchDisabled
                "
                @click="
                  handleSearch
                ">
                ค้นหา
              </el-button>

              <el-button
                type="info"
                :disabled="
                  !isClickDownloadAsCSVDisabled
                "
                @click.prevent="
                  downloadFileReport
                ">
                ดาวน์โหลด
              </el-button>

              <el-button
                plain
                type="danger"
                :disabled="
                  !isClearDisabled
                "
                @click="
                  handleClickClear
                ">
                รีเซ็ต
              </el-button>
            </div>

        </div>
      </div>

      <div class="report-content col-12">
        <div>
          <div
            v-show="latestUpdateTime"
            class="report-header"
          >
            <div >
              <b>รายงานอัปเดต</b>
              <p>
                {{ latestUpdateMessage() }}
              </p>
            </div>
            <div>
              <div>
                <p>
                  {{ totalCount(total, limit, reports.length, page) }}
                </p>
              </div>
              <div v-if="total !== 0" align="right">
                <el-button
                  v-for="
                    i in [10, 20, 30]
                  "
                  :key="i"
                  circle
                  size="small"
                  :type="
                    limit == i ? 'primary' : ''
                  "
                  @click="
                    limit = i
                  ">
                  {{ i }}
                </el-button>
              </div>
            </div>
          </div>

          <div class="mt-3">
            <DataTable
              :data="reports"
              :headers="headers"
              :loading="isLoading"
            >
              <template #editTransactionStatus>
                <el-table-column
                  label="Operation"
                  width="120"
                >
                  <template slot-scope="scope">
                    <span
                      v-if="scope.row.transaction_status !== 'PENDING'"
                    >-</span>
                    <DropdownUpdateEarnReport
                      v-else
                      fieldName="transaction_status"
                      :optionList="filterTransactionStatus"
                      v-on:onUpdateResult="fetchReports"
                      :defaultValue="scope.row.transaction_status"
                      :id="scope.row.id"
                    />
                  </template>
                </el-table-column>
              </template>
            </DataTable>
          </div>

          <div style="text-align: center;">
            <el-pagination
              background
              layout="
                prev, pager, next
              "
              :total="total"
              :disabled="
                !isPaginationDisable
              "
              :current-page="page"
              :page-size="limit"
              @current-change="
                handleCurrentChange
              ">
            </el-pagination>
          </div>
        </div>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
import { DateTime } from "luxon";
import { backendAdminClient } from "@/dependencies"
import { toLocaleTH } from "@/lib/dateTime"

// Components
import LoadingOverlay from '@/lib/components/LoadingOverlay'
import PageWrapper from "@/lib/components/PageWrapper";
import DatePicker from "@/lib/components/DatePicker";
import ScButton from "@/lib/components/ScButton";
import ProjectSelect from "@/lib/components/ProjectSelect";
import DataTable from "@/lib/components/DataTable.vue";

// Utils
import { formatNumber } from '@/lib/formatter'
import { validateDateRangeLimit } from '@/lib/dateTime'

// MNC Report
import DropdownUpdateEarnReport from "../component/DropdownUpdateEarnReport";
import { mappingDeprecatedEvent } from '../utils/mapHelper'

export default {
  components: {
    LoadingOverlay,
    PageWrapper,
    DatePicker,
    ScButton,
    ProjectSelect,
    DropdownUpdateEarnReport,
    DataTable
  },
  data() {
    const filterCompany = [{ label: "ทั้งหมด", value: undefined }];
    const filterTransactionStatus = [
      { label: "ทั้งหมด", value: undefined },
      { label: "SUCCESS", value: "SUCCESS" },
      { label: "PENDING", value: "PENDING" }
    ];
    const filterTransactionType = [
      { label: "TRANSFER (ปิดโอน)", value: "TRANSFER" },
      { label: 'CLAIM (กดรับ Reward "จากการโอนซื้อบ้านใหม่")', value: "CLAIM" },
      { label: "ROLLBACK (ถอยปิดโอน โดยยังไม่กดรับ Reward)", value: "ROLLBACK" },
      { label: "ROLLBACK_REFUND (ถอยปิดโอน โดยมีการกดรับ Reward ไปแล้ว)", value: "ROLLBACK_REFUND" },
      { label: 'EXPIRED (Reward "จากการโอนซื้อบ้านใหม่" หมดอายุ) ', value: "EXPIRED" },
    ]

    const headers = [
      {
        label: "วันที่บันทึกรายการ (RJ)",
        key: "created_at",
        width: '130px',
        formatter: ({ created_at }) => created_at
          ? this.dateFormat(created_at)
          : '-'
      },
      {
        label: "วันที่บันทึกรายการ (SC Sales)",
        key: "sc_sale_date",
        width: '160px',
        formatter: ({ sc_sale_date }) => sc_sale_date
          ? this.dateFormat(sc_sale_date)
          : '-'
      },
      {
        label: "วันที่โอนกรรมสิทธิ์",
        key: "transfer_date",
        width: '120px',
        formatter: ({ transfer_date }) => transfer_date
          ? this.dateFormat(transfer_date)
          : '-'
      },
      {
        label: "บริษัท (รหัส)",
        key: "company_code",
        width: '80px'
      },
      {
        label: "โครงการ",
        key: "project_code",
        width: '90px'
      },
      {
        label: "Unit",
        key: "unit_no",
        width: '100px'
      },
      {
        label: "บ้านเลขที่",
        key: "home_address_no",
        width: '100px'
      },
      {
        label: "มูลค่าบ้านสุทธิ (บาท)",
        key: "net_price",
        width: '130px',
        formatter: ({ net_price }) => (
          formatNumber(net_price, 0)
        )
      },
      {
        label: "จำนวน MNC 1% จากราคา",
        key: "mnc_coin_amount",
        width: '140px',
        formatter: ({ mnc_coin_amount }) => (
          formatNumber(mnc_coin_amount / 100, 0)
        )
      },
      {
        label: "มูลค่า MNC (บาท)",
        key: "mnc_coin_value",
        width: '115px',
        formatter: ({ mnc_coin_value }) => (
          formatNumber(mnc_coin_value / 100, 0)
        )
      },
      {
        label: "วันที่ได้รับ MNC",
        key: "mnc_received_date",
        width: '100px',
        formatter: ({ mnc_received_date }) => mnc_received_date
          ? this.dateFormat(mnc_received_date)
          : '-'
      },
      {
        label: "จำนวนที่ได้รับ MNC (MNC)",
        key: "mnc_received_amount",
        formatter: ({ mnc_received_amount }) => (
          mnc_received_amount
          ? formatNumber(mnc_received_amount / 100, 0)
          : '-'
        )
      },
      {
        label: "จำนวนได้รับ MNC (บาท)",
        key: "mnc_received_value",
        formatter: ({ mnc_received_value }) => (
          mnc_received_value
          ? formatNumber(mnc_received_value / 100, 0)
          : '-'
        )
      },
      {
        label: "Event ที่ได้รับ MNC",
        key: "mnc_event_name",
        formatter: ({ mnc_event, mnc_event_name }) => (
          mnc_event_name || mappingDeprecatedEvent(mnc_event)
        )
      },
      {
        label: "สถานะจาก SC Sale (ปิดโอน/ถอยปิดโอน)",
        key: "sc_sale_status",
        width: '118px'
      },
      {
        label: "Transaction Type",
        key: "transaction_type",
        width: '110px'
      },
      {
        label: "Transaction Status",
        key: "transaction_status",
        width: '115px'
      },
      {
        label: "ชื่อผู้รับ MNC",
        key: "mnc_recipient_name"
      },
      {
        label: "รหัสอ้างอิงตัวบุคคล",
        key: "reference_code"
      },
      {
        label: "User ID",
        key: "user_id",
        width: '70px'
      },
      {
        label: "วันที่ Expired (นับจากวัน โอน 90 วัน)",
        key: "expired_date",
        width: '130px',
        formatter: ({ expired_date }) => expired_date
          ? this.dateFormat(expired_date)
          : '-'
      },
      {
        label: "Refund Date",
        key: "refund_date",
        width: '100px',
        formatter: ({ refund_date }) => refund_date
          ? this.dateFormat(refund_date)
          : '-'
      },
    ]

    // Set Default Formatter
    headers.forEach((header, i) => {
      if (header.formatter) return
      headers[i].formatter = (row) => row[header.key] || '-'
    })

    const filterInput = {
      transferDateRange: [],
      receiveMNCDateRange: [],
      expiredDateRange: [],
      refundDateRange: []
    }

    const selectInputFilter = {
      filterCompany,
      filterTransactionStatus,
      filterTransactionType,
      selectedProject: undefined,
      selectedCompany: undefined,
      selectedTransactionStatus: undefined,
      selectedUpdateTransactionStatus: '',
      selectedTransactionType: [],
    }

    const isDisabledInput = {
      isDropdownDisabled: true,
      isSearchDisabled: true,
      isClickDownloadWithIncentiveAsCSVDisabled: true,
      isClickDownloadAsCSVDisabled: true,
      isClearDisabled: true,
      isPaginationDisable: true,
    }

    const pagination = {
      page: 1,
      limit: 10,
      total: 0,
      latestUpdateTime: DateTime.utc(),
    }

    return {
      ...filterInput,
      ...isDisabledInput,
      ...selectInputFilter,
      ...pagination,
      isLoading: true,
      reports: [],
      headers,
    };
  },
  mounted() {
    this.queryBuilder();
    this.getCompanyCode();
    this.fetchReports();
  },
  watch: {
    async "$route.query"() {
      this.queryBuilder();
      await this.fetchReports();
      this.$refs.pageEl.$el.querySelector(".page-content").scrollTop = 0;
    },
    async limit (from, to) {
      if (from != to) {
        await this.handleSearch()
      }
    }
  },
  computed: {
    queryParams() {
      const {
        transferDateRange,
        selectedProject: projectCode,
        selectedCompany: companyCode,
        selectedTransactionStatus: transactionStatus,
        selectedTransactionType: transactionType,
        receiveMNCDateRange,
        expiredDateRange,
        refundDateRange,
        page,
        limit
      } = this.$route.query;
      return {
        companyCode,
        transactionStatus,
        transactionType,
        projectCode,
        mncReceivedDate: this.convertDateRange(receiveMNCDateRange),
        refundDate: this.convertDateRange(refundDateRange),
        transferDate: this.convertDateRange(transferDateRange),
        expiredDate: this.convertDateRange(expiredDateRange),
        page: (page && +page) || 1,
        limit: +limit || this.limit
      }
    }
  },
  methods: {
    totalCount(total, limit, reportsLength, page) {
      if (!total) return "ไม่พบข้อมูล"

      const currentPage = +page || 1
      const isLastPage = limit > reportsLength

      const end = isLastPage
        ? total : currentPage * limit
      const start = isLastPage
        ? limit * (currentPage - 1) + 1
        : (currentPage - 1) * limit + 1

      return `แสดงผล ${start.toLocaleString()} - ${end.toLocaleString()} จากทั้งหมด ${total.toLocaleString()}`
    },
    queryBuilder() {
      const {
        transferDateRange,
        projectId,
        selectedProject,
        selectedCompany,
        selectedTransactionStatus,
        selectedTransactionType,
        receiveMNCDateRange,
        expiredDateRange,
        refundDateRange,
        page,
        limit
      } = this.$route.query;
      this.projectId = projectId || null;
      this.selectedProject = selectedProject || null;
      this.selectedCompany = selectedCompany || null;
      this.selectedTransactionStatus = selectedTransactionStatus || null;
      if (selectedTransactionType) {
        this.selectedTransactionType = Array.isArray(selectedTransactionType)
          ? selectedTransactionType
          : [selectedTransactionType];
      } else {
        this.selectedTransactionType = [];
      }
      this.transferDateRange = this.setupQueryDateRange(transferDateRange);
      this.receiveMNCDateRange = this.setupQueryDateRange(receiveMNCDateRange);
      this.expiredDateRange = this.setupQueryDateRange(expiredDateRange);
      this.refundDateRange = this.setupQueryDateRange(refundDateRange);
      this.page = (page && +page) || 1;
      this.limit = +limit || this.limit
    },
    handleSearch() {
      const projectCode =
        (this.selectedProject && this.selectedProject.projectCode) ||
        this.selectedProject;
      const {
        selectedCompany,
        selectedTransactionStatus,
        selectedTransactionType,
        receiveMNCDateRange,
        expiredDateRange,
        refundDateRange,
        transferDateRange,
        limit
      } = this;
      let query = {
        selectedProject: projectCode,
        selectedCompany,
        selectedTransactionStatus,
        selectedTransactionType,
        transferDateRange: this.convertDateRange(transferDateRange),
        receiveMNCDateRange: this.convertDateRange(receiveMNCDateRange),
        expiredDateRange: this.convertDateRange(expiredDateRange),
        refundDateRange: this.convertDateRange(refundDateRange),
        limit,
        page: 1
      };
      // NOTE: filter out null value
      query = Object.fromEntries(
        Object.entries(query).filter(([, value]) => {
          return (
            value !== null &&
            value !== undefined &&
            value !== "" &&
            (!Array.isArray(value) || value.length > 0)
          );
        })
      );
      // NOTE: replace route with query string
      const route = {
        name: this.$route.name,
        query: query
      };
      this.$router.replace(route);
    },
    setupQueryDateRange(dateRange) {
      if (dateRange && dateRange.length > 1) {
        const [from, to] = dateRange;
        return [DateTime.fromISO(from), DateTime.fromISO(to)];
      } else {
        return [];
      }
    },
    convertDateRange(dateRange) {
      if (dateRange && dateRange.length > 1) {
        return dateRange.map(date => DateTime.fromISO(date).toISO());
      }
    },
    handleClickClear() {
      const route = {
        name: this.$route.name
      };
      this.$router.replace(route);
    },
    handleCurrentChange(page) {
      const route = {
        name: this.$route.name,
        query: { ...this.$route.query, page: (page && +page) || 1 }
      };
      this.$router.replace(route);
    },
    async fetchReports() {

      // Initial Default
      this.isLoading = true

      try {

        // Query Report
        const query = this.queryParams;

        // Query Report
        const { data } = (
          await backendAdminClient.report.getTransferReport(query)
        )

        // Validate Response
        if (data) {

          // Get Data
          const { reports, pagination, timestamp } = data.result

          // Set State
          this.reports = reports
          this.latestUpdateTime = timestamp

          // Pagination
          this.total = pagination.total_records
        }
      }
      catch (error) {
        console.warn('Transfer Report Error', error);
        this.total = 0
        this.reports = []
        this.latestUpdateTime = DateTime.utc()
      }
      finally {
        this.isLoading = false
      }
    },
    async downloadFileReport(event) {
      event.preventDefault()


      try {

        // Get Query
        const query = this.queryParams

        // Validate Report Result
        if (this.reports.length === 0) {
          this.$alert(
            'ไม่พบข้อมูลที่ต้องการดาวน์โหลด',
            'แจ้งเตือน', {
              confirmButtonText: 'ปิด', type: 'warning'
          })
          return
        }

        // Get Daterange
        const {
          mncReceivedDate,
          refundDate,
          transferDate,
          expiredDate,
        } = query

        // Validate Daterange
        const dateRange = [mncReceivedDate, refundDate, transferDate, expiredDate].filter(e => e)

        // Validator
        if (dateRange.length === 0) {
          this.$alert(
            'กรุณาระบุวันที่ได้รับ MNC และระบุช่วงเวลาได้ไม่เกิน 1 เดือน',
            'แจ้งเตือน', {
              confirmButtonText: 'ปิด',
              type: 'warning'
          })
          return
        }

        // Validate Rang Limit
        const isLimit = dateRange.every(date => validateDateRangeLimit(
          date[0], date[1]
        ))

        // Range Limit Validator
        if (isLimit) {
          this.$alert(
            'ระบุช่วงเวลาได้ไม่เกิน 1 เดือน',
            'แจ้งเตือน', {
              confirmButtonText: 'ปิด',
              type: 'warning'
          })
          return
        }

        // Warning Download Message
        await this.$confirm(
          'การดาวน์โหลดจะใช้เวลาดำเนินการสักครู่ กรุณาอย่าออกจากหน้าจอ หรือรีเฟรชหน้า',
          'แจ้งเตือน', {
            confirmButtonText: 'ดาวน์โหลด',
            cancelButtonText: 'ปิด',
            type: 'info'
          }
        )

        // Set Loading
        this.isLoading = true

        // Update Download Query
        query.download = true

        // Send Request
        const { data } = await backendAdminClient.report.getTransferReport(query)

        // Get Date
        const dateFilename = this.dateFormat(
          DateTime.utc(), 'yyyy-MM-dd-hmmss'
        )

        // Transform Data
        const blob = new Blob([data])

        // Create DOM Element
        const link = document.createElement("a")
        link.href = window.URL.createObjectURL(blob)
        link.download = `${dateFilename}-mnc-transfer-reports.xlsx`

        // Download File
        link.click()

        // Clear Element
        link.remove()
      }
      catch (error) {
        console.warn(error)
      }
      finally {
        // Set Un-Loading
        this.isLoading = false
      }
    },
    dateFormat(date, format = "dd/MM/yyyy") {
      if (typeof date === 'object') date = DateTime.fromObject(date.c)
      return toLocaleTH(
        DateTime.fromISO(date)
      ).toFormat(format)
    },
    latestUpdateMessage() {
      const format = "dd MMMM yyyy เวลา HH:mm น."
      return this.dateFormat(this.latestUpdateTime, format)
    },
    async getCompanyCode() {
      const initialList = [{ label: "ทั้งหมด", value: undefined }]

      // Get unique data
      const { data } = (
        await backendAdminClient.report.getEarnReportCompany()
      )

      const mappingList = data.result.map(item => ({
        label: item.company_code,
        value: item.company_code
      }));

      // Set State
      this.filterCompany = initialList.concat(mappingList)
    },
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";
.header {
  z-index: 1;
}

.page-container {
  padding: 15px;
}

.report-cell {
  a {
    text-decoration: underline;
  }

  padding: 5px;
  border-left: 1px solid rgb(206, 204, 204);
  border-bottom: 1px solid rgb(228, 225, 225);

  &:empty::before {
    content: "N/A";
    color: #d8d8d8;
  }
}

.page-container {
  padding: 1rem;
  margin: 0;
}

.report-filter {
  width: 100%;
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.report-filter .form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form .section {
  display: grid;
  gap: 1rem;
  margin-top: 15px;
  grid-template-columns: repeat(2, 1fr);
}

.form .section[col="3"] {
  grid-template-columns: repeat(3, 1fr);
}

.label {
  font-size: .9rem;
  margin-bottom: .25rem;
}

.report-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.report-content .report-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.el-button.el-button--danger.is-plain:hover,
.el-button.el-button--danger.is-plain:focus {
  color: white !important;
}

@media screen and (max-width: 640px) {
  .form .section,
  .form .section[col="3"] {
    grid-template-columns: 1fr;
  }

  .report-filter {
    margin-bottom: 1rem;
  }
}

.el-input__inner {
  text-align: center;
}

.el-date-editor--daterange,
.el-input__inner {
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/features/morning-coin/pages/TransferHomeReportPage.vue